import { useCallback, useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import type { ChatMessage } from '@lemontaigu/chat';
import { getReceptionStore } from './data/store.js';
import type { ThreadSummary } from './data/receptionStore.js';
import { applyIncomingMessage, hasUnread, loadLastSeen, receptionText, saveLastSeen, sortThreads } from './logic.js';
import { fmtTime, LANG_LABELS, MessageList } from './messageDisplay.js';

// Reception dashboard (Increment C3): thread list (left) + conversation
// (right). Reception reads/writes FRENCH — guest originals show under the FR
// translation for reference; replies are translated server-side
// (/api/chat/staff-message). Realtime keeps both panes live.

/** Short attention beep on a new guest message (best-effort — autoplay rules may block). */
function beep(): void {
  try {
    const ctx = new AudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.08, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.35);
    osc.onended = () => void ctx.close();
  } catch {
    // No audio available — badge alone.
  }
}

export function Reception() {
  const store = getReceptionStore();
  const [threads, setThreads] = useState<ThreadSummary[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [flaggedIds, setFlaggedIds] = useState<Set<number>>(new Set());
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [lastSeen, setLastSeen] = useState<Record<string, number>>(() => loadLastSeen(localStorage));
  const selectedIdRef = useRef<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  selectedIdRef.current = selectedId;

  const reloadThreads = useCallback(async () => {
    setThreads(sortThreads(await store.listThreads()));
  }, [store]);

  // Initial load + live inserts.
  useEffect(() => {
    void reloadThreads();
    const unsubscribe = store.onMessage((message) => {
      setThreads((prev) => {
        const next = applyIncomingMessage(prev, message);
        if (!next) {
          void reloadThreads(); // new conversation — fetch it
          return prev;
        }
        return next;
      });
      if (message.conversationId === selectedIdRef.current) {
        setMessages((prev) => (prev.some((m) => m.id === message.id) ? prev : [...prev, message]));
        markSeen(message.conversationId, message.id);
      }
      if (message.sender === 'guest') beep();
    });
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function markSeen(conversationId: string, messageId: number): void {
    setLastSeen((prev) => {
      if ((prev[conversationId] ?? 0) >= messageId) return prev;
      const next = { ...prev, [conversationId]: messageId };
      saveLastSeen(localStorage, next);
      return next;
    });
  }

  async function select(thread: ThreadSummary): Promise<void> {
    setSelectedId(thread.conversation.id);
    setError('');
    const msgs = await store.listMessages(thread.conversation.id);
    setMessages(msgs);
    const newest = msgs.at(-1);
    if (newest) markSeen(thread.conversation.id, newest.id);
    // Flags load best-effort — the thread must render even if the lookup fails.
    try {
      const aiIds = msgs.filter((m) => m.sender === 'ai').map((m) => m.id);
      setFlaggedIds(new Set(await store.listFlaggedMessageIds(aiIds)));
    } catch {
      setFlaggedIds(new Set());
    }
  }

  /** « Réponse incorrecte » on an AI bubble (C7) — feeds the C9 review queue. */
  async function toggleFlag(message: ChatMessage): Promise<void> {
    const wasFlagged = flaggedIds.has(message.id);
    try {
      if (wasFlagged) await store.unflagAiMessage(message.id);
      else await store.flagAiMessage(message.id);
      setFlaggedIds((prev) => {
        const next = new Set(prev);
        if (wasFlagged) next.delete(message.id);
        else next.add(message.id);
        return next;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'signalement impossible');
    }
  }

  async function sendReply(e: FormEvent): Promise<void> {
    e.preventDefault();
    const body = draft.trim();
    if (!selectedId || !body || busy) return;
    setBusy(true);
    setError('');
    try {
      const message = await store.sendReply(selectedId, body);
      setDraft('');
      setMessages((prev) => (prev.some((m) => m.id === message.id) ? prev : [...prev, message]));
      setThreads((prev) => applyIncomingMessage(prev, message) ?? prev);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'envoi impossible');
    } finally {
      setBusy(false);
    }
  }

  async function toggleStatus(): Promise<void> {
    const selected = threads.find((t) => t.conversation.id === selectedId);
    if (!selected) return;
    const next = selected.conversation.status === 'open' ? 'closed' : 'open';
    await store.setStatus(selected.conversation.id, next);
    await reloadThreads();
  }

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages]);

  const selected = threads.find((t) => t.conversation.id === selectedId) ?? null;

  return (
    <div className="reception">
      <aside className="thread-list">
        <h2>Conversations</h2>
        {threads.length === 0 && <p className="muted">Aucune conversation pour l’instant.</p>}
        <ul>
          {threads.map((t) => (
            <li key={t.conversation.id}>
              <button
                className={[
                  'thread-item',
                  t.conversation.id === selectedId ? 'is-selected' : '',
                  t.conversation.status === 'closed' ? 'is-closed' : '',
                ].join(' ')}
                onClick={() => void select(t)}
              >
                <span className="thread-room">
                  Ch. {t.conversation.room}
                  <span className="thread-lang">{LANG_LABELS[t.conversation.guestLang]}</span>
                  {t.lastMessage && <span className="thread-time">{fmtTime(t.lastMessage.createdAt)}</span>}
                  {hasUnread(t, lastSeen[t.conversation.id]) && <span className="thread-unread" aria-label="non lu" />}
                </span>
                <span className="thread-preview">
                  {t.lastMessage ? receptionText(t.lastMessage) : '—'}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </aside>

      <section className="thread-view">
        {!selected ? (
          <p className="muted thread-empty">Sélectionnez une conversation.</p>
        ) : (
          <>
            <header className="thread-header">
              <h2>
                Chambre {selected.conversation.room}
                <span className="thread-lang">{LANG_LABELS[selected.conversation.guestLang]}</span>
              </h2>
              <button className="thread-status-btn" onClick={() => void toggleStatus()}>
                {selected.conversation.status === 'open' ? 'Clore' : 'Rouvrir'}
              </button>
            </header>

            <div className="thread-messages" ref={scrollRef}>
              <MessageList
                messages={messages}
                flaggedIds={flaggedIds}
                onToggleFlag={(m) => void toggleFlag(m)}
              />
            </div>

            {error && <p className="thread-error">{error}</p>}

            <form className="thread-composer" onSubmit={(e) => void sendReply(e)}>
              <input
                type="text"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Répondre en français — le client reçoit la traduction…"
                maxLength={2000}
              />
              <button type="submit" disabled={busy || draft.trim() === ''}>
                Envoyer
              </button>
            </form>
          </>
        )}
      </section>
    </div>
  );
}
